import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton
} from "@mui/material";

/* ✅ ProDev Assets & Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";

const bgImage = "https://images.unsplash.com/photo-1542831371-29b0f74f9713";

/* ✅ Styled Components - ProDev Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": { backgroundColor: "#e0b200", transform: "scale(1.05)" },
}));

const LiveDemoBox = styled(Box)(({ theme }) => ({
    padding: "20px",
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    border: "1px dashed goldenrod",
    textAlign: "center",
    marginTop: "15px"
}));

/**
 * HTMLManipulation Component
 * Specialized ProDevConsult Module
 * Author: Hazzan
 */
export default function HTMLManipulation() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* ✅ Refs & States for Live Interaction */
    const textRef = useRef(null);
    const colorRef = useRef(null);
    const containerRef = useRef(null);
    const [elementCount, setElementCount] = useState(0);

    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) { setIsSpeaking(false); return; }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ✅ ProDev Logic: DOM Injection */
    const addProDevElement = () => {
        if (elementCount < 4) {
            let newElement = document.createElement("p");
            newElement.innerHTML = `🚀 ProDev Node Added (${elementCount + 1})`;
            newElement.style.color = "goldenrod";
            containerRef.current.appendChild(newElement);
            setElementCount(prev => prev + 1);
        } else {
            alert("ProDev Alert: Limit reached! Optimization requires clean DOM management.");
        }
    };

    const domModules = useMemo(() => [
        {
            title: "1. Content Injection",
            tag: "innerHTML / textContent",
            desc: "Updating the content of an element is the most basic form of DOM manipulation. We use innerHTML to update the DOM tree dynamically.",
            code: `// ProDev Method: Target by ID and inject content\ndocument.getElementById("status").innerHTML = "Node Updated!";`,
            action: () => (textRef.current.innerHTML = "ProDev Status: Updated!"),
            buttonLabel: "Execute Injection"
        },
        {
            title: "2. CSS Object Model (CSSOM)",
            tag: "Style Manipulation",
            desc: "You can modify any CSS property via JavaScript. This allows for real-time theme changes and interactive UI feedback.",
            code: `// Update background via style object\nconst node = document.getElementById("proDevBox");\nnode.style.backgroundColor = "goldenrod";`,
            action: () => (colorRef.current.style.backgroundColor = "goldenrod"),
            buttonLabel: "Apply ProDev Theme"
        },
        {
            title: "3. Dynamic Node Creation",
            tag: "appendChild",
            desc: "Instead of just hiding elements, Hazzan often creates and injects new nodes into the document fragment for a truly dynamic app.",
            code: `// Create and append a new paragraph\nconst newNode = document.createElement("p");\nnewNode.innerHTML = "New ProDev Element";\nparent.appendChild(newNode);`,
            action: addProDevElement,
            buttonLabel: "Spawn New Node"
        }
    ], [elementCount]);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h4" : "h3"} sx={{
                    fontWeight: 800, backgroundColor: "goldenrod", color: "#100720",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    display: "inline-block", boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                }}>
                    DOM ARCHITECTURE
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                HTML Manipulation Essentials
                            </Typography>
                            <IconButton onClick={() => handleSpeak("JavaScript provides methods to select, modify, and interact with HTML elements. In this ProDevConsult module, we explore dynamic DOM interactions.")} sx={{ color: "goldenrod" }}>
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
                            The Document Object Model (DOM) is an interface that allows scripts to update the content, structure, and style of a document while it is being viewed. Mastering selection and injection is key to high-performance frontend engineering.
                        </Typography>
                    </SectionCard>
                </Grid>

                {domModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}><AiOutlineRead /></Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                        <Typography variant="caption" color="goldenrod" fontWeight={800}>{item.tag}</Typography>
                                    </Box>
                                </Box>
                                <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                    <IconButton onClick={() => handleCopy(item.code, i)} sx={{ color: "white" }}>
                                        {copiedIndex === i ? <CheckIcon /> : <ContentCopyIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>

                            <LiveDemoBox>
                                {i === 0 && <Typography ref={textRef} variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>Original ProDev Text</Typography>}
                                {i === 1 && <Box ref={colorRef} sx={{ width: "80px", height: "80px", backgroundColor: "crimson", mx: "auto", borderRadius: "10px", mb: 2 }}></Box>}
                                {i === 2 && <Box ref={containerRef} sx={{ mb: 2 }}></Box>}

                                <Button variant="contained" sx={{ bgcolor: "goldenrod", color: "black", fontWeight: "bold" }} onClick={item.action}>
                                    {item.buttonLabel}
                                </Button>
                            </LiveDemoBox>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/javascriptcoding" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">CONTINUE TO NEXT MODULE</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={() => window.speechSynthesis.cancel()}><StopIcon /></Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: "goldenrod" }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
